"use client";

/**
 * 谷歌地图组件
 * 
 * 基于 @react-google-maps/api 封装的谷歌地图组件，提供地图显示、位置标记、定位等功能
 * 支持显示当前位置、多个坐标标记，以及坐标点击交互
 */

// React
import React, { useCallback, useState } from "react";
// 三方库/框架
import { GoogleMap, Marker } from '@react-google-maps/api';
// 公共方法
import { getGeolocationAddressInfo } from "@/utils/addressUtils";
import { useGoogleMapsApi } from '@/hooks/useGoogleMapsApi';
// 接口
import { Address, GeolocationLocation } from "@/apis/address/address.interface";
// 组件
import UKLoadingIcon from "@/components/UKLoadingIcon";
// 资源
import LocationIcon from "@/assets/icons/address/LocationIcon.svg";
import PositionPinIconPng from "@/assets/images/address/PositionPinIcon.png";
import NormalPinIconPng from "@/assets/images/address/NormalPinIcon.png";
import ClickPinIconPng from "@/assets/images/address/ClickPinIcon.png";
// 枚举
import { ADDRESS_VERIFY_TYPE } from "@/constants/address";
// 样式
import styles from "./index.module.scss";

interface LocationPoint {
  lat: number; // 纬度
  lng: number; // 经度
  id?: string | number; // 坐标唯一标识 可选
}

interface GoogleMapComponentProps {
  currentLocation: LocationPoint; // 当前位置坐标
  locationPoints?: LocationPoint[]; // 需要在地图上标记的坐标点数组
  width?: string | number; // 地图容器宽度
  height?: string | number; // 地图容器高度
  className?: string; // 自定义样式
  hasBottomOverlay?: boolean; // 是否有底部浮层 用于调整LocationIcon位置
  verifyType?: number; // 地址信息查询校验类型 默认不校验
  onLocationChange?: (position: GeolocationLocation, addressInfo: Address | null | undefined) => void; // 位置变更回调
  onLocationPointClick?: (point: LocationPoint, index: number) => void; // 点击坐标回调
}

const GoogleMapComponent: React.FC<GoogleMapComponentProps> = ({
  currentLocation,
  locationPoints = [],
  width,
  height,
  className,
  hasBottomOverlay = false,
  verifyType = ADDRESS_VERIFY_TYPE.NO_VERIFY,
  onLocationChange,
  onLocationPointClick
}) => {
  
  // 地图是否已加载
  const { isLoaded } = useGoogleMapsApi();
  
  // 选中的坐标
  const [selectedPointId, setSelectedPointId] = useState<string | number | null>(null);

  // 获取当前位置方法
  const getCurrentLocation = useCallback(async () => {
    const { position, addressInfo } = await getGeolocationAddressInfo({
      options: {
        webOptions: {
          enableHighAccuracy: true, // 启用高精度定位
          timeout: 10000, // 超时时间10秒
          maximumAge: 0 // 不使用缓存的位置信息
        }
      },
      verifyType
    });

    if(!position?.lat || !position?.lng) {
      return;
    }
    
    // 回调通知父组件位置变更
    if (onLocationChange && position) {
      onLocationChange(position, addressInfo);
    }
  }, [onLocationChange, verifyType]);

  /**
   * 处理坐标点击事件
   * 设置选中的坐标ID，并通过onLocationPointClick回调通知父组件
   */
  const handleLocationPointClick = useCallback((point: LocationPoint, index: number) => {
    setSelectedPointId(point.id || `${point.lat}-${point.lng}`);
    onLocationPointClick?.(point, index);
  }, [onLocationPointClick]);

  return (
    <div className={`${styles.mapContainer} ${className || ''}`} style={{ ...(height ? { height } : {}), ...(width ? { width } : {}) }}>
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={{ height: "100%", width: "100%" }}
          center={currentLocation}
          zoom={14}
          options={{
            mapTypeId: 'roadmap',
            disableDefaultUI: true, // 禁用默认UI控件
          }}
        >
          {/* 当前位置标记 */}
          {currentLocation && (
            <Marker
              position={currentLocation}
              icon={{
                url: PositionPinIconPng.src,
                scaledSize: isLoaded ? new window.google.maps.Size(40, 40) : undefined,
                anchor: isLoaded ? new window.google.maps.Point(20, 40) : undefined
              }}
            />
          )}
          
          {/* 坐标点标记列表 */}
          {locationPoints.map((point, index) => {
            const pointId = point.id || `${point.lat}-${point.lng}`;
            const isSelected = pointId === selectedPointId;
            
            return (
              <Marker
                key={pointId}
                position={point}
                onClick={() => handleLocationPointClick(point, index)}
                icon={{
                  url: isSelected ? ClickPinIconPng.src : NormalPinIconPng.src, // 根据选中状态显示不同图标
                  scaledSize: isLoaded ? new window.google.maps.Size(34, 36) : undefined,
                  anchor: isLoaded ? new window.google.maps.Point(17, 36) : undefined
                }}
              />
            );
          })}
          
          {/* 定位按钮 - 点击获取当前位置 */}
          <div 
            className={`${styles.mapLocationIcon} ${hasBottomOverlay ? styles.elevated : ''}`}
            onClick={getCurrentLocation}
          >
            <LocationIcon />
          </div>
        </GoogleMap>
      ) : (
        // 地图加载中loading
        <div className={styles.mapLoading}>
          <UKLoadingIcon type="black" />
        </div>
      )}
    </div>
  );
};

export default GoogleMapComponent;
